import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { FormSection, FormActions } from './ui';
import { useFormValidation } from '../hooks/useFormValidation';
import api from '../utils/api';

const UserCreateForm = ({ onSubmit, onCancel }) => {
  const { t } = useTranslation();

  const initialData = {
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'member',
    tckn: '',
    memberType: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    registrationStatus: 'active',
    membershipPlan: ''
  };

  const validationRules = {
    username: [
      (value) => !value?.trim() ? t('admin:createUser.validation.usernameRequired') : null,
      (value) => value?.length < 3 ? t('admin:createUser.validation.usernameTooShort') : null,
      (value) => value?.length > 30 ? t('admin:createUser.validation.usernameTooLong') : null,
      (value) => !/^[a-zA-Z0-9_]+$/.test(value) ? t('admin:createUser.validation.usernameInvalid') : null
    ],
    email: [
      (value) => !value?.trim() ? t('admin:createUser.validation.emailRequired') : null,
      (value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? t('admin:createUser.validation.emailInvalid') : null
    ],
    password: [
      (value) => !value ? t('admin:createUser.validation.passwordRequired') : null,
      (value) => value?.length < 8 ? t('admin:createUser.validation.passwordTooShort') : null,
      (value) => !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])/.test(value) ? t('admin:createUser.validation.passwordWeak') : null
    ],
    confirmPassword: [
      (value, data) => value !== data.password ? t('admin:createUser.validation.passwordMismatch') : null
    ],
    firstName: [
      (value) => !value?.trim() ? t('admin:createUser.validation.firstNameRequired') : null,
      (value) => value?.length > 50 ? t('admin:createUser.validation.firstNameTooLong') : null
    ],
    lastName: [
      (value) => !value?.trim() ? t('admin:createUser.validation.lastNameRequired') : null,
      (value) => value?.length > 50 ? t('admin:createUser.validation.lastNameTooLong') : null
    ],
    role: [
      (value) => !['member', 'trainer', 'admin', 'receptionist'].includes(value) ? t('admin:createUser.validation.roleInvalid') : null
    ],
    tckn: [
      (value) => !value?.trim() ? t('admin:createUser.validation.tcknRequired') : null,
      (value) => !/^\d{11}$/.test(value) ? t('admin:createUser.validation.tcknInvalid') : null
    ],
    memberType: [
      (value) => !value ? t('admin:createUser.validation.memberTypeRequired') : null
    ],
    emergencyContactName: [
      (value) => !value?.trim() ? t('admin:createUser.validation.emergencyNameRequired') : null
    ],
    emergencyContactRelationship: [
      (value) => !value?.trim() ? t('admin:createUser.validation.emergencyRelationshipRequired') : null
    ],
    emergencyContactPhone: [
      (value) => !value?.trim() ? t('admin:createUser.validation.emergencyPhoneRequired') : null,
      (value) => !/^(\+90|0)?[5][0-9]{9}$/.test(value?.replace(/\s/g, '')) ? t('admin:createUser.validation.emergencyPhoneInvalid') : null
    ]
  };

  const {
    data: formData,
    isSubmitting,
    updateField,
    handleSubmit,
    getFieldError,
    hasFieldError
  } = useFormValidation(initialData, validationRules);

  const [plans, setPlans] = useState([]);
  const [memberTypes, setMemberTypes] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [memberTypesLoading, setMemberTypesLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    fetchPlans();
    fetchMemberTypes();
  }, []);

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await api.get('/plans');
      setPlans(response.data.data || []);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchMemberTypes = async () => {
    try {
      setMemberTypesLoading(true);
      const response = await api.get('/member-types');
      setMemberTypes(response.data.data || []);
    } catch (err) {
      console.error('Error fetching member types:', err);
      setMemberTypes([]);
    } finally {
      setMemberTypesLoading(false);
    }
  };

  const onFormSubmit = async (data) => {
    try {
      setApiError('');

      const submitData = {
        username: data.username.trim(),
        email: data.email.trim(),
        password: data.password,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        role: data.role,
        tckn: data.tckn.trim(),
        memberType: data.memberType,
        emergencyContact: {
          name: data.emergencyContactName.trim(),
          relationship: data.emergencyContactRelationship.trim(),
          phone: data.emergencyContactPhone.trim()
        },
        registrationStatus: data.registrationStatus
      };

      // Add membership plan if provided
      if (data.membershipPlan) {
        submitData.membershipPlan = data.membershipPlan;
      }

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error creating user:', error);
      setApiError(error.response?.data?.error || t('admin:createUser.messages.createError'));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError('')}>
          {apiError}
        </Alert>
      )}

      {/* Account Information Section */}
      <FormSection
        title={t('admin:createUser.sections.account')}
        description={t('admin:createUser.sections.accountDescription')}
        variant="card"
      >
        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('admin:createUser.fields.username')}
              required
              value={formData.username}
              onChange={(e) => updateField('username', e.target.value)}
              placeholder={t('admin:createUser.placeholders.username')}
              error={hasFieldError('username')}
              helperText={getFieldError('username')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="email"
              label={t('admin:createUser.fields.email')}
              required
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder={t('admin:createUser.placeholders.email')}
              error={hasFieldError('email')}
              helperText={getFieldError('email')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label={t('admin:createUser.fields.password')}
              required
              value={formData.password}
              onChange={(e) => updateField('password', e.target.value)}
              placeholder={t('admin:createUser.placeholders.password')}
              error={hasFieldError('password')}
              helperText={getFieldError('password') || t('admin:createUser.passwordHint')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label={t('admin:createUser.fields.confirmPassword')}
              required
              value={formData.confirmPassword}
              onChange={(e) => updateField('confirmPassword', e.target.value)}
              placeholder={t('admin:createUser.placeholders.confirmPassword')}
              error={hasFieldError('confirmPassword')}
              helperText={getFieldError('confirmPassword')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Personal Information Section */}
      <FormSection
        title={t('admin:createUser.sections.personal')}
        description={t('admin:createUser.sections.personalDescription')}
        variant="card"
      >
        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('admin:createUser.fields.firstName')}
              required
              value={formData.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              placeholder={t('admin:createUser.placeholders.firstName')}
              error={hasFieldError('firstName')}
              helperText={getFieldError('firstName')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('admin:createUser.fields.lastName')}
              required
              value={formData.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              placeholder={t('admin:createUser.placeholders.lastName')}
              error={hasFieldError('lastName')}
              helperText={getFieldError('lastName')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('admin:createUser.fields.tckn')}
              required
              value={formData.tckn}
              onChange={(e) => updateField('tckn', e.target.value)}
              placeholder={t('admin:createUser.placeholders.tckn')}
              error={hasFieldError('tckn')}
              helperText={getFieldError('tckn')}
              inputProps={{ maxLength: 11 }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required disabled={memberTypesLoading} error={hasFieldError('memberType')}>
              <InputLabel>{t('admin:createUser.fields.memberType')}</InputLabel>
              <Select
                value={formData.memberType}
                onChange={(e) => updateField('memberType', e.target.value)}
                label={t('admin:createUser.fields.memberType')}
              >
                {memberTypes.map((type) => (
                  <MenuItem key={type._id} value={type._id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
              {hasFieldError('memberType') && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {getFieldError('memberType')}
                </Typography>
              )}
              {memberTypesLoading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} />
                  {t('admin:createUser.loading.memberTypes')}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </FormSection>

      {/* Emergency Contact Section */}
      <FormSection
        title={t('admin:createUser.sections.emergency')}
        description={t('admin:createUser.sections.emergencyDescription')}
        variant="card"
      >
        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('admin:createUser.fields.emergencyName')}
              required
              value={formData.emergencyContactName}
              onChange={(e) => updateField('emergencyContactName', e.target.value)}
              placeholder={t('admin:createUser.placeholders.emergencyName')}
              error={hasFieldError('emergencyContactName')}
              helperText={getFieldError('emergencyContactName')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('admin:createUser.fields.emergencyRelationship')}
              required
              value={formData.emergencyContactRelationship}
              onChange={(e) => updateField('emergencyContactRelationship', e.target.value)}
              placeholder={t('admin:createUser.placeholders.emergencyRelationship')}
              error={hasFieldError('emergencyContactRelationship')}
              helperText={getFieldError('emergencyContactRelationship')}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('admin:createUser.fields.emergencyPhone')}
              required
              value={formData.emergencyContactPhone}
              onChange={(e) => updateField('emergencyContactPhone', e.target.value)}
              placeholder={t('admin:createUser.placeholders.emergencyPhone')}
              error={hasFieldError('emergencyContactPhone')}
              helperText={getFieldError('emergencyContactPhone')}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Role & Membership Section */}
      <FormSection
        title={t('admin:createUser.sections.role')}
        description={t('admin:createUser.sections.roleDescription')}
        variant="card"
      >
        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required error={hasFieldError('role')}>
              <InputLabel>{t('admin:createUser.fields.role')}</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => updateField('role', e.target.value)}
                label={t('admin:createUser.fields.role')}
              >
                <MenuItem value="member">{t('admin:createUser.roles.member')}</MenuItem>
                <MenuItem value="trainer">{t('admin:createUser.roles.trainer')}</MenuItem>
                <MenuItem value="receptionist">{t('admin:createUser.roles.receptionist')}</MenuItem>
                <MenuItem value="admin">{t('admin:createUser.roles.admin')}</MenuItem>
              </Select>
              {hasFieldError('role') && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {getFieldError('role')}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('admin:createUser.fields.registrationStatus')}</InputLabel>
              <Select
                value={formData.registrationStatus}
                onChange={(e) => updateField('registrationStatus', e.target.value)}
                label={t('admin:createUser.fields.registrationStatus')}
              >
                <MenuItem value="active">{t('admin:createUser.status.active')}</MenuItem>
                <MenuItem value="pending_verification">{t('admin:createUser.status.pendingVerification')}</MenuItem>
                <MenuItem value="payment_pending">{t('admin:createUser.status.paymentPending')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth disabled={plansLoading}>
              <InputLabel>{t('admin:createUser.fields.membershipPlan')}</InputLabel>
              <Select
                value={formData.membershipPlan}
                onChange={(e) => updateField('membershipPlan', e.target.value)}
                label={t('admin:createUser.fields.membershipPlan')}
              >
                <MenuItem value="">
                  <em>{t('admin:createUser.noPlan')}</em>
                </MenuItem>
                {plans.filter(p => p.isActive).map((plan) => (
                  <MenuItem key={plan._id} value={plan._id}>
                    {plan.name} - {plan.price} TL ({plan.duration} {t('admin:createUser.days')})
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                {plansLoading && <CircularProgress size={14} />}
                {plansLoading ? t('admin:createUser.loading.plans') : t('admin:createUser.planHint')}
              </Typography>
            </FormControl>
          </Grid>
        </Grid>
      </FormSection>

      {/* Form Actions */}
      <FormActions
        primaryAction={{
          label: t('admin:createUser.buttons.create'),
          loading: isSubmitting,
          disabled: isSubmitting
        }}
        secondaryAction={{
          label: t('admin:createUser.buttons.cancel'),
          onClick: onCancel,
          disabled: isSubmitting
        }}
        alignment="right"
        variant="sticky"
      />
    </Box>
  );
};

export default UserCreateForm;
